import { useCallback, useEffect, useState } from "react";
import { App, Button, Card, Empty, Form, InputNumber, Space, Table, Tag, Typography } from "antd";
import type { AtRiskClient, AtRiskParams, AtRiskResponse } from "../../types/clientsAtRisk";
import { ActiveSubscriptionSwitch } from "../../components/ActiveSubscriptionSwitch";

const { Title, Paragraph, Text } = Typography;

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

const DEFAULTS: AtRiskParams = {
  zero_ratio: 0.3,
  min_searches: 5,
  inactive_days: 21,
  window_days: 14,
  active_only: true,
};

function adminHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function adminFetch<T>(url: string): Promise<T> {
  const response = await fetch(url, { headers: adminHeaders() });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const normalized = value.replace(/(\.\d{3})\d+/, "$1");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("pl-PL");
}

const REASON_LABELS: Record<string, { label: string; color: string }> = {
  high_zero_results: { label: "dużo zero-wyników", color: "error" },
  inactive: { label: "brak aktywności", color: "warning" },
};

export const ClientsAtRiskList = () => {
  const { notification } = App.useApp();

  const [params, setParams] = useState<AtRiskParams>(DEFAULTS);
  const [rows, setRows] = useState<AtRiskClient[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (active: AtRiskParams) => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          zero_ratio: String(active.zero_ratio),
          min_searches: String(active.min_searches),
          inactive_days: String(active.inactive_days),
          window_days: String(active.window_days),
        });
        if (active.active_only) query.set("active_only", "true");
        const response = await adminFetch<AtRiskResponse>(
          `${API_URL}/api/v1/admin/clients/at-risk?${query.toString()}`,
        );
        setRows(response.data ?? []);
        setTotal(response.total ?? 0);
      } catch (error) {
        setRows([]);
        setTotal(0);
        notification.error({
          message: "Nie udało się pobrać listy klientów z problemami",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        setLoading(false);
      }
    },
    [notification],
  );

  useEffect(() => {
    load(DEFAULTS);
  }, [load]);

  const patch = (next: Partial<AtRiskParams>) => setParams((prev) => ({ ...prev, ...next }));

  return (
    <Card>
      <Title level={4}>Klienci z problemami</Title>
      <Paragraph type="secondary">
        Sygnał ryzyka churn dla opiekunów: klienci z wysokim odsetkiem zero-wyników w oknie
        analizy albo bez aktywności wyszukiwania od dłuższego czasu. Progi są parametryzowane.
      </Paragraph>

      <Form layout="vertical">
        <Space wrap size={16} align="end">
          <Form.Item label="Min. odsetek zero-wyników" tooltip="0–1, np. 0.3 = 30%">
            <InputNumber
              min={0}
              max={1}
              step={0.05}
              value={params.zero_ratio}
              onChange={(v) => patch({ zero_ratio: v ?? DEFAULTS.zero_ratio })}
            />
          </Form.Item>
          <Form.Item label="Min. liczba wyszukań (próbka)">
            <InputNumber
              min={1}
              value={params.min_searches}
              onChange={(v) => patch({ min_searches: v ?? DEFAULTS.min_searches })}
            />
          </Form.Item>
          <Form.Item label="Nieaktywny od (dni)">
            <InputNumber
              min={1}
              value={params.inactive_days}
              onChange={(v) => patch({ inactive_days: v ?? DEFAULTS.inactive_days })}
            />
          </Form.Item>
          <Form.Item label="Okno analizy zero-wyników (dni)">
            <InputNumber
              min={1}
              max={365}
              value={params.window_days}
              onChange={(v) => patch({ window_days: v ?? DEFAULTS.window_days })}
            />
          </Form.Item>
          <Form.Item>
            <div style={{ height: 32, display: "flex", alignItems: "center" }}>
              <ActiveSubscriptionSwitch
                checked={!!params.active_only}
                onChange={(checked) => patch({ active_only: checked })}
              />
            </div>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={() => load(params)}>
                Zastosuj
              </Button>
              <Button
                onClick={() => {
                  setParams(DEFAULTS);
                  load(DEFAULTS);
                }}
              >
                Domyślne
              </Button>
            </Space>
          </Form.Item>
        </Space>
      </Form>

      <Paragraph type="secondary">
        Znaleziono <Text strong>{total}</Text> klientów z sygnałem ryzyka.
      </Paragraph>

      <Table<AtRiskClient>
        rowKey={(record) => record.user_id}
        dataSource={rows}
        loading={loading}
        locale={{ emptyText: <Empty description="Brak klientów spełniających kryteria — dobra wiadomość." /> }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        scroll={{ x: true }}
      >
        <Table.Column<AtRiskClient>
          dataIndex="email"
          title="Klient"
          render={(value: string | undefined, record) => value || <Text code>{record.user_id}</Text>}
        />
        <Table.Column<AtRiskClient>
          title="Sygnały"
          render={(_: unknown, record) => (
            <Space size={[4, 4]} wrap>
              {record.reasons.map((reason) => {
                const meta = REASON_LABELS[reason] ?? { label: reason, color: "default" };
                return (
                  <Tag key={reason} color={meta.color}>
                    {meta.label}
                  </Tag>
                );
              })}
            </Space>
          )}
        />
        <Table.Column<AtRiskClient>
          dataIndex="zero_ratio"
          title="Zero-wyników"
          width={140}
          sorter={(a, b) => a.zero_ratio - b.zero_ratio}
          render={(value: number, record) => (
            <Text type={value >= 0.3 ? "danger" : undefined}>
              {Math.round((value ?? 0) * 100)}% ({record.zero_results}/{record.search_count})
            </Text>
          )}
        />
        <Table.Column<AtRiskClient>
          dataIndex="days_since_last_search"
          title="Ostatnie wyszukanie"
          width={200}
          sorter={(a, b) => (a.days_since_last_search ?? 0) - (b.days_since_last_search ?? 0)}
          render={(value: number | null, record) => (
            <>
              {formatDate(record.last_search_at)}
              {value != null ? <Text type="secondary"> ({value} dni temu)</Text> : null}
            </>
          )}
        />
      </Table>
    </Card>
  );
};
